/**
 * Registers a focus trap for the given dialog. Returns a cleanup function that removes all registered listeners.
 * @param {HTMLDialogElement} dialog Dialog element to trap focus within.
 * @returns {() => void} Cleanup function.
 */
export default function registerTabTrap(dialog) {
    const tabbableElements = /** @type {HTMLElement[]} */ (Array.from(dialog.querySelectorAll('a, button, input'))
        .filter((tabbableElement) => {
            // @ts-ignore disabled doesn't exist on Element but does exist on button/input
            if (['button', 'input'].includes(tabbableElement.tagName) && tabbableElement.disabled === true) {
                return false;
            }

            const computedStyle = window.getComputedStyle(tabbableElement);
            return !(computedStyle.display === 'none' || computedStyle.visibility === 'hidden');
        }));

    if (tabbableElements.length === 0) {
        return () => {
        };
    }

    const firstTabbableElement = tabbableElements.at(0);
    const lastTabbableElement = tabbableElements.at(-1);

    const focusInHandler = (event) => {
        if (!dialog.contains(event.target)) {
            event.preventDefault();
            firstTabbableElement.focus();
        }
    };
    document.addEventListener('focusin', focusInHandler);

    const firstKeydownHandler = (event) => {
        if (event.key === 'Tab' && event.shiftKey) {
            event.preventDefault();
            lastTabbableElement.focus();
        }
    };
    firstTabbableElement.addEventListener('keydown', firstKeydownHandler);

    const lastKeydownHandler = (event) => {
        if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            firstTabbableElement.focus();
        }
    };
    lastTabbableElement.addEventListener('keydown', lastKeydownHandler);

    return () => {
        document.removeEventListener('focusin', focusInHandler);
        firstTabbableElement.removeEventListener('keydown', firstKeydownHandler);
        lastTabbableElement.removeEventListener('keydown', lastKeydownHandler);
    };
}
